import { TouchEvent } from 'react';

import { ORDER_PATTERNS, STAMP_PATTERNS } from '../constants';
import { Point2D } from '../types';
import {
    getDistance, getAngle, getDeviation2, getDeviation1,
    getPoints,
} from '../utils';

interface Props {
    onSuccess?: Function;
    onFailure?: Function;
}

/**
 *  스탬프 제스처를 인식하는 커스텀 훅.
 *
 * 동작 흐름:
 * 1. 터치 이벤트에서 4개의 터치 좌표를 추출 (handleTouchStart)
 * 2. 4점이 이루는 사각형의 최적 순서(최소 둘레)를 탐색 (checkStamp)
 * 3. 해당 순서로 내각과 변의 비율을 계산해 스탬프 패턴과 대조 (retrieveStamp)
 */
function useStampSheet({ onSuccess, onFailure }: Props) {
    /**
     * 터치 시작 이벤트 핸들러.
     * 중복 터치 제거 후 정확히 5개의 독립된 터치 포인트가 있을 때만 스탬프 검사를 실행한다.
     */
    function handleTouchStart(e: TouchEvent<HTMLDivElement>) {
        const { touches } = e;
        // 30px 이내 중복 터치를 제거한 실제 포인트 목록
        const points = getPoints(touches);
        if (points.length === 5) {
            if (checkStamp(points)) {
                onSuccess?.();
            } else {
                onFailure?.();
            }
        }
    }

    function isBetween(min: number, max: number, value: number) {
        return value >= min && value <= max;
    }

    /**
     * 스탬프 패턴 검증 함수.
     *
     * @param angleArr        - 오름차순 정렬된 5개의 내각(도 단위)
     * @param selectedLengthArr - 오름차순 정렬된 5개의 변 길이
     * @returns 스탬프 인식 성공 여부 (현재는 항상 true 반환 — 구현 중)
     *
     * 동작:
     * - 변 길이를 인접한 값들의 비율(ratio)로 정규화한다.
     *   (절대 크기가 아닌 형태(shape)로 비교하기 위해)
     *     ratio0 = 최솟값 / 최댓값  (가장 짧은 변 vs 가장 긴 변)
     *     ratio1 = 2번째 / 최솟값
     *     ratio2 = 3번째 / 2번째
     *     ratio3 = 4번째 / 3번째
     *     ratio4 = 최댓값 / 4번째
     * - 각 내각에 ±0.2°, 각 비율에 ±6의 허용 범위를 계산한다.
     * - (TODO) 계산된 범위가 기준 스탬프 패턴과 일치하는지 실제 비교 로직 필요.
     */
    function retrieveStamp(angleArr: number[], selectedLengthArr: number[]): boolean {
        // 정렬된 내각 5개 (오름차순)
        const [angle0, angle1, angle2, angle3, angle4] = angleArr;
        // 정렬된 변 길이 5개 (오름차순)
        const [len0, len1, len2, len3, len4] = selectedLengthArr;

        // 변 길이를 인접 비율로 변환 — 스탬프의 크기와 무관하게 형태만 비교
        const ratio0 = 1; // 최솟값 / 최댓값
        const ratio1 = len1 / len0; // 2번째 / 최솟값
        const ratio2 = len2 / len1; // 3번째 / 2번째
        const ratio3 = len3 / len2; // 4번째 / 3번째
        const ratio4 = len4 / len3; // 최댓값 / 4번째

        // 각 내각의 허용 범위 계산 (기준값 ± 6°)
        const [angle0up, angle0dn] = getDeviation1(angle0);
        const [angle1up, angle1dn] = getDeviation1(angle1);
        const [angle2up, angle2dn] = getDeviation1(angle2);
        const [angle3up, angle3dn] = getDeviation1(angle3);
        const [angle4up, angle4dn] = getDeviation1(angle4);

        // 각 변 비율의 허용 범위 계산 (기준값 ± 0.2)
        const [ratio0up, ratio0dn] = getDeviation2(ratio0);
        const [ratio1up, ratio1dn] = getDeviation2(ratio1);
        const [ratio2up, ratio2dn] = getDeviation2(ratio2);
        const [ratio3up, ratio3dn] = getDeviation2(ratio3);
        const [ratio4up, ratio4dn] = getDeviation2(ratio4);

        const matchedPattern = STAMP_PATTERNS.find((pattern) => isBetween(angle0dn, angle0up, pattern.angle0)
            && isBetween(angle1dn, angle1up, pattern.angle1)
            && isBetween(angle2dn, angle2up, pattern.angle2)
            && isBetween(angle3dn, angle3up, pattern.angle3)
            && isBetween(angle4dn, angle4up, pattern.angle4)
            && isBetween(ratio0dn, ratio0up, pattern.ratio0)
            && isBetween(ratio1dn, ratio1up, pattern.ratio1)
            && isBetween(ratio2dn, ratio2up, pattern.ratio2)
            && isBetween(ratio3dn, ratio3up, pattern.ratio3)
            && isBetween(ratio4dn, ratio4up, pattern.ratio4));

        return !!matchedPattern;
    }

    function sortArr(a: number, b: number) {
        if (a > b) {
            return 1;
        }
        if (a < b) {
            return -1;
        }
        return 0;
    }

    /**
     * 5개의 터치 좌표로부터 스탬프 형태를 분석하는 함수.
     *
     * @param positions - 터치 스크린 좌표 5개
     * @returns retrieveStamp 결과 또는 좌표가 5개 미만이면 false
     *
     * 동작:
     * 1. ORDER_PATTERNS의 24가지 순서로 5점을 이어 오각형을 구성하고,
     *    각 순서의 총 둘레(perimeter)를 계산한다.
     *    (최소 둘레 순서 = 자기교차가 없는 볼록 오각형에 가장 가까운 순서)
     * 2. 최소 둘레를 만드는 최적 순서를 선택한다.
     * 3. 선택된 순서의 5개 내각을 계산한다.
     * 4. 변 길이 배열과 내각 배열을 각각 오름차순 정렬 후 retrieveStamp에 전달한다.
     */
    function checkStamp(positions: Point2D[]) {
        if (positions.length !== 5) {
            return false;
        }
        let selectDistanceArr: number[] = [];
        let angleArr: number[] = [];
        let minDistance = -1;
        let selectOrder = -1;

        // 24가지 오각형 순서 중 둘레가 가장 짧은(= 가장 볼록한) 순서를 탐색
        for (let i = 0; i < 24; i += 1) {
            let totalDistance = 0;
            const distanceArr: number[] = [];
            for (let j = 0; j < 5; j += 1) {
                // 순서 패턴에 따라 인접한 두 점 사이의 거리를 계산
                const length = getDistance(
                    positions[ORDER_PATTERNS[i][j]],
                    positions[ORDER_PATTERNS[i][(j + 1) % 5]],
                );
                distanceArr.push(length);
                totalDistance += length;
            }
            // 현재 순서가 더 짧은 둘레를 가지면 최적 순서로 갱신
            if (minDistance === -1 || totalDistance < minDistance) {
                minDistance = totalDistance;
                selectOrder = i;
                selectDistanceArr = distanceArr;
            }
        }

        // 최적 순서로 5개의 꼭짓점 내각을 계산
        // getAngle(prev, cur, next) → cur에서의 내각
        for (let z = 0; z < 5; z += 1) {
            const angle = getAngle(
                positions[ORDER_PATTERNS[selectOrder][z]],
                positions[ORDER_PATTERNS[selectOrder][(z + 1) % 5]],
                positions[ORDER_PATTERNS[selectOrder][(z + 2) % 5]],
            );
            angleArr.push(angle);
        }

        // 정렬하여 절대적 위치와 무관하게 형태(shape)만 비교
        selectDistanceArr = selectDistanceArr.sort(sortArr);
        angleArr = angleArr.sort(sortArr);
        return retrieveStamp(angleArr, selectDistanceArr);
    }

    return {
        handleTouchStart,
    };
}

export default useStampSheet;
